import React from 'react';
import Select, { SelectOption } from '@amzn/meridian/select';
import { formatSelectedLabels, isGroupedFieldValue, FieldValue, validateFieldFormat } from '../../../utils/fieldUtils';
import { FONT_SIZE_BODY, TEXT_COLOR_PRIMARY } from '../cssConstants';

interface FieldOption {
  value: string;
  label: string;
}

interface EditFieldRendererProps {
  fieldName: string;
  value: FieldValue | undefined;
  displayValue: string;
  isMultiselect: boolean;
  isSingleSelect: boolean;
  hasNotApplicableOption: boolean;
  filteredOptions: FieldOption[];
  hierarchyMapping?: Record<string, string[]>;
  parentFieldValues?: string[];
  searchQuery: string;
  placeholder?: string;
  label?: string;
  onFieldChange: (fieldName: string, value: FieldValue) => void;
  onSearchChange: (fieldName: string, query: string) => void;
}

const EditFieldRenderer: React.FC<EditFieldRendererProps> = ({
  fieldName,
  value,
  displayValue,
  isMultiselect,
  isSingleSelect,
  hasNotApplicableOption,
  filteredOptions,
  hierarchyMapping,
  parentFieldValues,
  searchQuery,
  placeholder,
  label,
  onFieldChange,
  onSearchChange,
}) => {
  const isTextField = !isSingleSelect && !isMultiselect;

  if (!isTextField && (isGroupedFieldValue(value) || (parentFieldValues && parentFieldValues.length > 0))) {
    const grouped = isGroupedFieldValue(value) ? value : {};
    const existingKeys = Object.keys(grouped);
    const allKeys = parentFieldValues
      ? Array.from(new Set(parentFieldValues.concat(existingKeys))).filter(k => {
          if (parentFieldValues.includes(k)) return true;
          const v = grouped[k];
          if (!v) return false;
          return Array.isArray(v) ? v.some(x => x !== '<UNKNOWN>') : v !== '<UNKNOWN>';
        })
      : existingKeys;

    return (
      <div>
        {allKeys.map((parent) => {
          const raw = grouped[parent];
          const vals: string[] = Array.isArray(raw) ? raw : (raw ? [raw] : []);
          const childValues = hierarchyMapping?.[parent];
          const parentOptions = childValues
            ? filteredOptions.filter(opt => childValues.includes(opt.value))
            : filteredOptions;
          // A single-select child stores one value per parent (Dict[str, str]); a
          // multiselect stores a list (Dict[str, List[str]]).
          const singleVal = vals.find(v => v !== '<UNKNOWN>') || '';
          return (
            <div key={parent} style={{ marginBottom: '8px' }}>
              <span style={{ fontSize: '11px', fontWeight: '500', color: TEXT_COLOR_PRIMARY }}>
                {label ? `${label} for ${parent}` : `${parent}:`}
              </span>
              <Select
                value={isSingleSelect ? singleVal : vals.filter(v => v !== '<UNKNOWN>')}
                onChange={(newValue: string | string[]) => {
                  const next = isSingleSelect
                    ? (Array.isArray(newValue) ? newValue[0] : newValue) || '<UNKNOWN>'
                    : (Array.isArray(newValue) ? newValue : [newValue]);
                  const updated = { ...grouped, [parent]: next } as FieldValue;
                  onFieldChange(fieldName, updated);
                }}
                valueLabel={(opts: any[]) => formatSelectedLabels(opts)}
                placeholder={isSingleSelect ? 'Select an option...' : 'Select options...'}
                popoverMaxHeight={300}
                width="100%"
                size="small"
              >
                {parentOptions.map(opt => (
                  <SelectOption key={opt.value} value={opt.value} label={opt.label} />
                ))}
              </Select>
            </div>
          );
        })}
      </div>
    );
  }

  if (isMultiselect && filteredOptions) {
    const selectValue = Array.isArray(value) ? value.filter(v => v !== '<UNKNOWN>') : (value && value !== '<UNKNOWN>' ? [value as string] : []);
    return (
      <Select
        value={selectValue}
        onChange={(newValue: string | string[]) => {
          const newArr = Array.isArray(newValue) ? newValue : [newValue];
          const currentArr = Array.isArray(value) ? value : (value ? [value] : []);
          const wasNotApplicable = currentArr.includes('<NOT_APPLICABLE>');
          const nowHasNotApplicable = newArr.includes('<NOT_APPLICABLE>');
          if (nowHasNotApplicable && !wasNotApplicable) {
            // Selecting "Not Applicable" clears all other selections
            onFieldChange(fieldName, ['<NOT_APPLICABLE>']);
          } else if (wasNotApplicable && newArr.length > 1) {
            // Selecting something else while "Not Applicable" is selected removes it
            onFieldChange(fieldName, newArr.filter(v => v !== '<NOT_APPLICABLE>'));
          } else {
            onFieldChange(fieldName, newArr);
          }
        }}
        valueLabel={(opts: any[]) => formatSelectedLabels(opts)}
        searchQuery={searchQuery}
        onSearch={(query: string) => onSearchChange(fieldName, query)}
        label={label}
        placeholder="Select options..."
        popoverMaxHeight={300}
        width="100%"
        size="small"
      >
        {hasNotApplicableOption && (
          <SelectOption key="<NOT_APPLICABLE>" value="<NOT_APPLICABLE>" label="Not Applicable" />
        )}
        {filteredOptions.map(opt => (
          <SelectOption key={opt.value} value={opt.value} label={opt.label} />
        ))}
      </Select>
    );
  }

  if (isSingleSelect && filteredOptions) {
    return (
      <Select
        value={value && value !== '<UNKNOWN>' ? (value as string) : ''}
        onChange={(newValue: string | string[]) => {
          const val = Array.isArray(newValue) ? newValue[0] : newValue;
          onFieldChange(fieldName, val || '<UNKNOWN>');
        }}
        searchQuery={searchQuery}
        onSearch={(query: string) => onSearchChange(fieldName, query)}
        label={label}
        placeholder="Select an option..."
        popoverMaxHeight={300}
        width="100%"
        size="small"
      >
        {hasNotApplicableOption && (
          <SelectOption key="<NOT_APPLICABLE>" value="<NOT_APPLICABLE>" label="Not Applicable" />
        )}
        {filteredOptions.map(opt => (
          <SelectOption key={opt.value} value={opt.value} label={opt.label} />
        ))}
      </Select>
    );
  }

  // Text field
  const isFormatInvalid = placeholder && displayValue && !validateFieldFormat(displayValue, placeholder);
  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '6px 8px',
    border: `1px solid ${isFormatInvalid ? '#d32f2f' : '#ccc'}`,
    borderRadius: '4px',
    fontSize: FONT_SIZE_BODY,
    boxSizing: 'border-box',
  };
  return (
    <div>
      {placeholder ? (
        // Date/time format fields stay single-line.
        <input
          type="text"
          value={displayValue}
          placeholder={placeholder}
          onChange={(e) => onFieldChange(fieldName, e.target.value)}
          style={inputStyle}
        />
      ) : (
        // Free text: grow to fit so the whole value is visible.
        <textarea
          value={displayValue}
          onChange={(e) => onFieldChange(fieldName, e.target.value)}
          rows={Math.min(
            20,
            displayValue
              .split('\n')
              .reduce((n, line) => n + Math.max(1, Math.ceil(line.length / 50)), 0) || 1,
          )}
          style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.4, fontFamily: 'inherit' }}
        />
      )}
      {isFormatInvalid && (
        <span style={{ fontSize: '11px', color: '#d32f2f', marginTop: '2px', display: 'block' }}>
          Expected format: {placeholder}
        </span>
      )}
    </div>
  );
};

export default EditFieldRenderer;
